import { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { audioFeedback } from '@/shared/api/audioFeedback';

interface LargeIconButtonProps {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'md' | 'lg' | 'xl';
  badge?: number | string;
  disabled?: boolean;
  className?: string;
  /**
   * FIX-CAPTURE-TRANCHE-2B-W3 (SD-T40): suppress the spoken label. The button still vibrates
   * and plays the tap sound; only the TTS announcement is skipped. Used by the voice capture
   * card, where speaking an English "Yes"/"No" over a Lao question clip would be wrong.
   * Defaults to false, so every existing caller is unaffected.
   */
  silent?: boolean;
  'data-testid'?: string;
}

const sizeClasses = {
  md: 'w-14 h-14',    // 56px
  lg: 'w-16 h-16',    // 64px
  xl: 'w-20 h-20',    // 80px
};

const iconSizes = {
  md: 'w-7 h-7',
  lg: 'w-8 h-8',
  xl: 'w-10 h-10',
};

export const LargeIconButton: React.FC<LargeIconButtonProps> = ({
  icon: Icon,
  label,
  onClick,
  variant = 'primary',
  size = 'lg',
  badge,
  disabled = false,
  className,
  silent = false,
  'data-testid': testId,
}) => {
  const handleClick = () => {
    if (disabled) return;

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(15);
    }

    // Audio feedback
    audioFeedback.play('tap');

    // Voice announcement if enabled (never when `silent` — the label must not talk over a clip)
    const voiceEnabled = !silent && localStorage.getItem('agxl_voice_enabled') !== 'false';
    if (voiceEnabled && window.speechSynthesis) {
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const utterance = new SpeechSynthesisUtterance(label);
      utterance.lang = lang;
      utterance.rate = 1.1;
      window.speechSynthesis.speak(utterance);
    }

    onClick();
  };

  const variantClasses = {
    primary: 'bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90',
    secondary: 'bg-primary/10 text-primary hover:bg-primary/20',
    ghost: 'bg-muted text-muted-foreground hover:bg-muted/80',
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      data-testid={testId}
      className={cn(
        'relative flex items-center justify-center rounded-2xl',
        'active:scale-95 transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        sizeClasses[size],
        variantClasses[variant],
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
    >
      <Icon className={cn(iconSizes[size], 'transition-transform')} />

      {/* Badge */}
      {badge !== undefined && (
        <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-bold px-1.5 py-0.5 rounded-full min-w-5 text-center shadow-sm">
          {badge}
        </span>
      )}
    </button>
  );
};
